import type { ListResult, ListState, ResourceClient } from './types.js';
import { appliedFilters } from './list-state.js';

/**
 * The error shape every endpoint returns (`@aimmo/contracts`). Declared
 * structurally rather than imported so the kit does not depend on the API
 * package — the contract's error schema is the thing both sides agree on.
 */
export interface KitApiError {
  code: string;
  message: string;
  fields?: Record<string, string[]>;
  requestId?: string;
}

export class ResourceRequestError extends Error {
  constructor(
    readonly status: number,
    readonly error: KitApiError,
  ) {
    super(error.message);
    this.name = 'ResourceRequestError';
  }
}

/**
 * Structural rather than `instanceof`.
 *
 * A monorepo can end up with two copies of this module — a bundler duplicating
 * it, a test importing through a different specifier — and then `instanceof`
 * quietly says no for an error that is exactly right. The failure mode is a
 * form that shows "errore imprevisto" instead of highlighting the field, which
 * is precisely the bug this file exists to prevent.
 */
export function isResourceRequestError(error: unknown): error is ResourceRequestError {
  if (error instanceof ResourceRequestError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { status?: unknown; error?: unknown };
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.error === 'object' &&
    candidate.error !== null &&
    'code' in candidate.error
  );
}

export function isForbidden(error: unknown): boolean {
  return isResourceRequestError(error) && error.status === 403;
}

/**
 * Minimal shape of a ts-rest client route — enough to call it, no more.
 *
 * `unknown` on both sides is deliberate: an initClient route is an overloaded
 * callable whose argument type depends on the contract, and restating that here
 * would couple the kit to ts-rest's internals. The adapter re-narrows on the
 * way out, and the resource's own generics carry the real types.
 */
type TsRestCall = (args: never) => Promise<{ status: number; body: unknown }>;

export interface TsRestCrudRouter {
  list: TsRestCall;
  get?: TsRestCall;
  create?: TsRestCall;
  update?: TsRestCall;
  remove?: TsRestCall;
}

/** The adapter builds plain argument objects; ts-rest's overloads reject them. */
function call(
  fn: TsRestCall,
  args: Record<string, unknown>,
): Promise<{ status: number; body: unknown }> {
  return (fn as (input: Record<string, unknown>) => Promise<{ status: number; body: unknown }>)(
    args,
  );
}

type Ok<T> = Extract<T, { status: 200 | 201 | 202 | 204 }>;

/**
 * A ts-rest result narrowed to its success branch, or the error thrown.
 *
 * One function for the kit's adapter and every hand-written call in the apps,
 * so a refusal is always the same `ResourceRequestError` a form knows how to
 * map onto its fields. Any 2xx counts: 202 is work accepted rather than done —
 * a queued recount — and the screen has nothing more to show for it.
 */
export function assertOk<T extends { status: number; body: unknown }>(result: T): Ok<T> {
  if (result.status >= 200 && result.status < 300) return result as Ok<T>;
  throw new ResourceRequestError(result.status, result.body as KitApiError);
}

/** The adapter's routes are untyped (see `TsRestCall`): the status is any number, the body unknown. */
function unwrap(result: { status: number; body: unknown }): unknown {
  assertOk(result);
  return result.body;
}

/**
 * Adapter for the standard CRUD contract: `list` takes `listQuerySchema`, the
 * others take `:id`. Filters are sent as flat query keys, which is what the API
 * repositories read.
 */
export function tsRestResourceClient<TItem, TCreate = unknown, TUpdate = unknown>(
  router: TsRestCrudRouter,
): ResourceClient<TItem, TCreate, TUpdate> {
  const client: ResourceClient<TItem, TCreate, TUpdate> = {
    list: async (state: ListState, signal?: AbortSignal) => {
      const query: Record<string, unknown> = {
        page: state.page,
        pageSize: state.pageSize,
        order: state.order,
        ...(state.sort ? { sort: state.sort } : {}),
        ...(state.q ? { q: state.q } : {}),
        ...appliedFilters(state),
      };
      return unwrap(
        await call(router.list, { query, fetchOptions: { signal } }),
      ) as ListResult<TItem>;
    },
  };

  if (router.get) {
    const get = router.get;
    client.get = async (id, signal) =>
      unwrap(await call(get, { params: { id }, fetchOptions: { signal } })) as TItem;
  }
  if (router.create) {
    const create = router.create;
    client.create = async (input) => unwrap(await call(create, { body: input })) as TItem;
  }
  if (router.update) {
    const update = router.update;
    client.update = async (id, input) =>
      unwrap(await call(update, { params: { id }, body: input })) as TItem;
  }
  if (router.remove) {
    const remove = router.remove;
    client.remove = async (id) => {
      unwrap(await call(remove, { params: { id } }));
    };
  }

  return client;
}
